/**
 * Mapping a Prozo pricing quote to a bookable channel.
 *
 * Prozo prices a *lane*, but you book a *channel*: `bluedart-air` and
 * `bluedart-surface` are separate channels at different prices, so a carrier
 * name alone cannot say which one an operator picked. Every flow that lets
 * someone choose a courier has to resolve the quote to one of these names.
 *
 * Extracted from the Orders tab so the label screen resolves channels
 * identically — the two must not drift.
 */

export const PROZO_CHANNEL_NAMES = [
  'dtdc-surface',
  'shadowfax-surface',
  'ats-surface',
  'bluedart-surface',
  'bluedart-air',
  'dtdc-air',
  'delhivery-air',
  'xpressbees-air',
  'delhivery-surface',
  // 'ekart-surface', // Temporarily disabled
  'xpressbees-surface',
  'shadowfax-sdd/ndd',
  'xpressbees-sdd/ndd',
  'pikndel-sdd/ndd',
  'blitz-sdd/ndd',
];

/**
 * Channel suffix for a quote.
 *
 * dispatch_mode has only two values, but SDD/NDD is a same/next-day service
 * tier layered on a SURFACE dispatch — so two modes yield three buckets.
 */
export function dispatchChannelSuffix(dispatchMode, serviceTypePrimary) {
  const stp = String(serviceTypePrimary || '').toUpperCase();
  if (stp.startsWith('SDD') || stp.includes('NDD') || stp.startsWith('INTERCITY')) {
    return 'sdd/ndd';
  }
  if (String(dispatchMode || '').toUpperCase() === 'AIR') {
    return 'air';
  }
  return 'surface';
}

/** Bucket suffix encoded by a PROZO_CHANNEL_NAMES entry. */
export function channelBucketSuffix(channel) {
  if (channel.includes('sdd/ndd')) return 'sdd/ndd';
  if (channel.endsWith('-air')) return 'air';
  return 'surface';
}

/** Parent portion of a channel name, normalised for fuzzy comparison. */
export function channelParentKey(channel) {
  return channel
    .replace(/-sdd\/ndd$/, '')
    .replace(/-(surface|air)$/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Resolve a quote to a bookable channel, or null when nothing matches.
 *
 * Fuzzy on the parent name, but constrained to the right dispatch bucket, so
 * an AIR quote can never book a SURFACE channel.
 */
export function resolveProzoChannel(providerName, dispatchMode, serviceTypePrimary) {
  const suffix = dispatchChannelSuffix(dispatchMode, serviceTypePrimary);
  const provider = String(providerName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (!provider) return null;

  return (
    PROZO_CHANNEL_NAMES.find((ch) => {
      if (channelBucketSuffix(ch) !== suffix) return false;
      const channelParent = channelParentKey(ch);
      return (
        channelParent === provider ||
        channelParent.includes(provider) ||
        provider.includes(channelParent)
      );
    }) || null
  );
}

/**
 * Flatten a Prozo pricing row into the fields a carrier card shows.
 *
 * Field names are Prozo's own, taken from how the Orders tab reads live
 * responses — `priceAfterTax` / `priceBeforeTax` for money, `providerMinTatDays`
 * for transit, and `provider.remarks` for the human service label.
 */
export function describeQuote(quote) {
  const provider = quote?.provider || {};
  const parent = provider.parent || provider.name || 'Unknown';
  // provider.dispatch_mode is authoritative; the top-level field mirrors it.
  const dispatchMode = provider.dispatch_mode || quote?.dispatch_mode || '';
  const serviceTypePrimary = provider.service_type_primary || '';

  const basePrice = quote?.priceBeforeTax || 0;
  const finalPrice = quote?.priceAfterTax || quote?.amount || 0;
  const taxAmount = quote?.tax || finalPrice - basePrice;

  const minTatDays = quote?.providerMinTatDays || quote?.minTatDays || 0;
  const maxTatDays = quote?.providerMaxTatDays || quote?.maxTatDays || 0;

  return {
    parent,
    providerId: provider.id || '',
    // Prozo's human remark if present, else the service code, else something
    // descriptive rather than blank.
    serviceName:
      provider.remarks || serviceTypePrimary || quote?.zone || quote?.paymentMode || 'Standard',
    serviceTypePrimary,
    dispatchMode,
    dispatchModeLabel: String(dispatchMode).toUpperCase(),
    basePrice,
    finalPrice,
    taxAmount,
    taxPercentage: basePrice > 0 ? ((taxAmount / basePrice) * 100).toFixed(1) : '0',
    minTatDays,
    maxTatDays,
    paymentMode: quote?.paymentMode || '',
    zone: quote?.zone || '',
    codAvailable: quote?.cod_available,
    channel: resolveProzoChannel(parent, dispatchMode, serviceTypePrimary),
  };
}

/**
 * Whether a reverse quote is a quality-checked pickup: 'QC', 'NOQC', or null
 * when the service code says neither (forward quotes, untagged rate cards).
 *
 * Prozo prices QC and NOQC as separate providers of the same carrier
 * (REV_REG_QC vs REV_REG_NOQC), and both resolve to the same channel.
 */
export function quoteQcKind(serviceTypePrimary) {
  const stp = String(serviceTypePrimary || '').toUpperCase();
  if (/NO[-_]?QC|NON[-_]?QC/.test(stp)) return 'NOQC';
  if (/(^|_)QC($|_)/.test(stp)) return 'QC';
  return null;
}

/**
 * Courier quotes reduced to what Tara needs to compare them, for the label and
 * manual-order wizards (see useTaraTerminalFocus in utils/taraTerminalContext).
 */
export function summarizeQuotesForTara(quotes) {
  return (quotes || []).map((quote) => {
    const q = describeQuote(quote);
    return {
      courier: q.parent,
      service: q.serviceName,
      mode: q.dispatchModeLabel || undefined,
      price_incl_tax: q.finalPrice,
      delivery_days: q.maxTatDays ? `${q.minTatDays}-${q.maxTatDays}` : undefined,
      cod_available: q.codAvailable,
      bookable: Boolean(q.channel),
    };
  });
}
